<template>
  <v-row no-gutters>
    <feature-card-basic :layer="layer" @update:feature="onFeatureUpdate">
      <template v-if="isDocumentAllowed" #top>
        <sidebar-row v-if="isFetched" class="my-4 d-flex">
          <v-row v-if="availableDocsCodes.length" no-gutters>
            <v-col cols="auto">
              <base-button
                :straight-text="$i18n.t('table.rziDocuments.addDocumentation')"
                @click="handleAddDocumentation()"
              ></base-button>
            </v-col>
          </v-row>
          <v-row v-if="documents && documents.length" no-gutters class="mt-6">
            <v-col cols="12">
              <ribbon-list :listItems="documents">
                <template #itemButtons-replace="{ item }">
                  <dots-menu
                    :items="getDotsItems(item)"
                    @editDocumentation="handleAddDocumentation(item)"
                    @deleteDocumentation="openDeleteDialog(item)"
                  />
                </template>
              </ribbon-list>
            </v-col>
          </v-row>
        </sidebar-row>
        <main-loader v-else />
      </template>
    </feature-card-basic>
    <add-edit-feature-dialog
      v-if="isEditFeatureLoaded"
      :key="addEditFeatureDialogKey"
      :is-visible.sync="isEditFeatureVisible"
      v-bind="editFeatureData"
      @done="handleEdit"
    />
    <main-dialog
      :v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :loading="isDeleteDialogLoading"
      :title="$i18n.t('table.rziDocuments.deleteDocument')"
      :body="$i18n.t('table.rziDocuments.deleteDocumentBody')"
      @agree="handleDelete"
      agree-text="dialog.agreeDelete"
    />
    <image-lightbox :items="currentImage" :visible.sync="imagePreviewVisible" enableZoom type="pdf" />
  </v-row>
</template>

<script>
import FeatureCardBasic from '@/components/FeatureCardBasic';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';
import RibbonList from '@/components/RibbonList';
import AddEditFeatureDialog from '@/components/AddEditFeatureDialog';
import DotsMenu from '@/components/DotsMenu';
import MainDialog from '@/components/MainDialog';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import { call, get } from 'vuex-pathify';
import { documentation } from '@/assets/js/rziData';
import { utils } from '@/mixins/rziHelpers';

export default {
  name: 'FeatureCardRziDocuments',
  mixins: [prepareAttributesFeatureCardMixin, utils],
  components: {
    FeatureCardBasic,
    MainLoader,
    SidebarRow,
    RibbonList,
    DotsMenu,
    MainDialog,
    AddEditFeatureDialog,
    ImageLightbox: () => import('@/components/ImageLightbox'),
  },
  props: {
    layer: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    isEditFeatureLoaded: false,
    isEditFeatureVisible: false,
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    itemToDelete: null,
    isFetched: false,
    documents: [],
    values: {},
    protectedSitesDatasourceSchemaUpdated: [],
    availableDocsCodes: [],
    isDocumentAllowed: false,
    imagePreviewVisible: false,
    currentImage: undefined,
  }),
  computed: {
    token: get('authentication/token'),
    dataSources: get('layers/metadata'),
    tableTabs: get('table/tabs'),
    currentFeatureId() {
      return this.$route.params.fid;
    },
    addEditFeatureDialogKey() {
      return `addeditdialog-${this.currentFeatureId}|${this.documents.length}`;
    },
  },
  watch: {
    tableTabs: {
      deep: true,
      async handler(nV, oV) {
        const newRziDocs = nV.filter(
          tab =>
            tab.value.startsWith('rziDocs') &&
            tab.documentFeature.properties[documentation.documents.attributes.docCode] === 'ZZO'
        );
        if (!newRziDocs.length) return;
        const triggerUpdate = newRziDocs.filter(tab => {
          const oldTabsProperty = oV.find(tabOld => tabOld.value === tab.value)?.documentFeature?.properties[
            documentation.documents.attributes.docTitle
          ];
          if (!oldTabsProperty) return false;
          return tab.documentFeature.properties[documentation.documents.attributes.docTitle] !== oldTabsProperty;
        });
        if (!(triggerUpdate || []).length) return;
        let isDocumentInView = triggerUpdate.some(tab =>
          this.documents.map(document => document.id).includes(tab.documentFeature.id)
        );
        if (isDocumentInView) await this.init();
      },
    },
  },
  methods: {
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    getDotsItems(item) {
      const docCode = item.feature.properties[documentation.documents.attributes.docCode];
      return [
        ...(docCode === 'SDF'
          ? [
              {
                name: 'editDocumentation',
              },
            ]
          : []),
        {
          name: 'deleteDocumentation',
        },
      ];
    },
    openDeleteDialog(item) {
      this.itemToDelete = item.id;
      this.isDeleteDialogLoaded = this.isDeleteDialogVisible = true;
    },
    async handleDelete() {
      this.isDeleteDialogLoading = true;
      try {
        await this.deleteDatasourceFeature({
          datasource: documentation.documents.datasource,
          id: this.itemToDelete,
          params: {
            cascade: true,
          },
        });
        await this.init();
        this.$store.set('table/CLOSE_TABLE_TAB!', `rziDocs_${this.itemToDelete}`);
        this.isDeleteDialogVisible = this.isDeleteDialogLoading = false;
      } catch {
        this.isDeleteDialogLoading = false;
      }
    },
    getEditFeatureData(item) {
      return {
        featureData: {
          datasource: documentation.documents.datasource,
          ...(item && { id: item?.feature.id }),
        },
        title: item
          ? this.$i18n.t('table.rziDocuments.editDocumentation')
          : this.$i18n.t('table.rziDocuments.addDocumentation'),
        customDatasourceSchema: this.protectedSitesDatasourceSchemaUpdated,
        hiddenAttributes: ['_systems', ...documentation.documents.hiddenAttributes],
        ...(item && { featureMetadata: item?.feature }),
        staticProperties: {
          [documentation.documents.attributes.mainArea]: +this.currentFeatureId,
          ...(item && {
            [documentation.documents.attributes.docType]:
              item.feature.properties[documentation.documents.attributes.docType],
          }),
        },
      };
    },
    async handleEdit(closeDialog) {
      closeDialog();
      await this.init();
    },
    onFeatureUpdate(feature) {
      this.values = { ...feature.properties };
      this.isDocumentAllowed = this.values[documentation.mainArea.attributes.typeCode].startsWith('N2K');
      this.init();
    },
    handleAddDocumentation(item) {
      let newDatasourceAttributes = JSON.parse(
        JSON.stringify(this.dataSources[documentation.documents.datasource].attributes_schema.attributes)
      );
      if (!item) {
        newDatasourceAttributes.find(
          attr => attr.name === documentation.documents.attributes.docType
        ).relation.filter_expression = JSON.stringify({
          $IN: {
            [`${documentation.documentsDict.datasource}.${documentation.documentsDict.attributes.code}`]: this
              .availableDocsCodes,
          },
        });
      }
      this.protectedSitesDatasourceSchemaUpdated = newDatasourceAttributes;
      this.editFeatureData = this.getEditFeatureData(item);
      this.isEditFeatureLoaded = this.isEditFeatureVisible = true;
    },
    openTableTab(document) {
      this.$store.set('table/ADD_TABLE_TAB!', {
        value: `rziDocs_${document.id}`,
        name: this.getDocumentTitle(this.values[documentation.mainArea.attributes.name], document.feature.properties),
        documentFormName: this.values[documentation.mainArea.attributes.name],
        documentFeature: document.feature,
      });
    },
    getAvailableDocsToAdd(features) {
      let availableCodes = [];
      const existingFeaturesCodes = features.map(ft => ft.properties[documentation.documents.attributes.docCode]);
      if (!existingFeaturesCodes.includes('SDF')) availableCodes.push('SDF');
      const filteredZZO = existingFeaturesCodes.filter(ft => ft === 'ZZO');
      if (filteredZZO.length > 0) {
        if (filteredZZO.length < 3) availableCodes.push('ZZO');
      } else {
        if (!existingFeaturesCodes.includes('PO') && !existingFeaturesCodes.includes('PZO'))
          availableCodes = [...availableCodes, 'PO', 'PZO', 'ZZO'];
      }
      return availableCodes;
    },
    getAttachment: call('relations/getAttachment'),
    async init() {
      this.isFetched = false;
      try {
        const r = await this.getDataSourceFeatures({
          dataSource: documentation.documents.datasource,
          filters: {
            $EQUAL: {
              [`${documentation.documents.datasource}.${documentation.documents.attributes.mainArea}`]: {
                value: +this.currentFeatureId,
              },
            },
          },
        });
        this.availableDocsCodes = this.getAvailableDocsToAdd(r.features);
        if (!r.features.length) return (this.documents = []);
        this.documents = r.features.map(ft => ({
          feature: ft,
          id: ft.id,
          itemClickHandler: async item => {
            const docCode = item.feature.properties[documentation.documents.attributes.docCode];
            switch (docCode) {
              case 'SDF': {
                const docLink = item.feature.properties[documentation.documents.attributes.link];
                if (docLink?.startsWith('/')) {
                  const fileId = docLink.split('/')[2];
                  const attachment = await this.getAttachment(fileId);
                  if (attachment.properties.filename?.toLowerCase().includes('.pdf')) this.onAttachmentPreview(fileId);
                } else if (docLink && this.$_isValidUrl(docLink)) window.open(docLink, '_blank');
                break;
              }
              default:
                return this.openTableTab(item);
            }
          },
          title: this.getDocumentTitle(null, ft.properties),
          codeId: ft.properties[documentation.documents.attributes.docType],
        }));
      } catch (error) {
        console.log(error);
      } finally {
        this.isFetched = true;
      }
    },
    onAttachmentPreview(id) {
      this.currentImage = [
        `${process.env.VUE_APP_API_URL}/attachments/${id}/file?as_attachment=false&token=${this.token}`,
      ];
      this.imagePreviewVisible = true;
    },
  },
};
</script>
